import React, { useEffect } from 'react';

import moment, { Moment } from 'moment';
import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import { loadOrgCriticalDates } from '../critical-dates/actions/load-org-critical-dates';

import { EventDayCard } from './components/event-day-card';

import { CalendarDisplayType } from './utils/calendar-display-type';

type InputProps = {
  orgId: number;
  criticalDatesByDate: { [date: string]: Array<CriticalDate> };
  calendarStart: Moment;
};

const mapDispatchToProps = (
  dispatch: ThunkDispatch<any, any, AnyAction>,
  { orgId }: InputProps,
) => ({
  actions: {
    loadOrgCriticalDates: (startDate: Moment) =>
      dispatch(
        loadOrgCriticalDates(
          orgId,
          moment(startDate).startOf('month').format('YYYY-MM-DD'),
          moment(startDate).endOf('month').add(1, 'day').format('YYYY-MM-DD'),
        ),
      ),
  },
});

type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = InputProps & DispatchProps;

const daysByWeek = (calendarStart: Moment) => {
  const firstWeekdayOfMonth = moment(calendarStart).startOf('month').day();
  const lastWeekdayOfMonth = moment(calendarStart).endOf('month').day();
  const days = [];
  [...Array(firstWeekdayOfMonth)].map(() => days.push(''));

  for (
    let date = moment(calendarStart).startOf('month');
    date.isSameOrBefore(moment(calendarStart).endOf('month'), 'day');
    date.add(1, 'day')
  ) {
    days.push(moment(date).format('YYYY-MM-DD'));
  }

  [...Array(6 - lastWeekdayOfMonth)].map(() => days.push(''));

  const weeks = [];
  for (let i = 0; i < days.length; i += 7) {
    weeks.push(days.slice(i, i + 7));
  }

  return weeks;
};

export const EventCalendarMonth = connect(
  null,
  mapDispatchToProps,
)(({ orgId, criticalDatesByDate, calendarStart, actions }: Props) => {
  const currentDisplayedDates = [...Array(moment(calendarStart).endOf('month').date())] // This gives us an empty array with the number of days in the month
    .map((_element, i) =>
      moment(calendarStart).startOf('month').add(i, 'days').format('YYYY-MM-DD'),
    );

  useEffect(() => {
    if (
      !criticalDatesByDate ||
      currentDisplayedDates.some((dateString) => !criticalDatesByDate[dateString])
    ) {
      actions.loadOrgCriticalDates(calendarStart);
    }
  }, [criticalDatesByDate, actions, calendarStart, currentDisplayedDates]);

  const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  return (
    <div className="card-body u-display-grid u-no-padding">
      <div className="u-display-grid u-grid-auto-flow-column u-grid-auto-columns-grow">
        {daysOfWeek.map((weekday, weekdayIndex) => (
          <div
            key={weekday}
            className={`u-textAlign-center u-border-bottom u-padding-sm ${
              weekdayIndex ? 'u-border-left' : ''
            }`}
          >
            {weekday}
          </div>
        ))}
      </div>
      <div
        className="u-display-grid u-grid-auto-flow-row u-grid-auto-rows-grow"
        style={{ height: '75vh' }}
      >
        {daysByWeek(calendarStart).map((week, weekIndex) => (
          <div
            key={`${week[0]}-${week[6]}`}
            className="u-display-grid u-grid-auto-flow-column u-grid-auto-columns-grow"
          >
            {week.map((day, dayOfWeekIndex) => (
              <div
                key={`${day || `empty-day-${dayOfWeekIndex}`}`}
                className={`u-height-100 u-overflow-auto ${day ? '' : 'u-background-offWhite'} ${
                  dayOfWeekIndex ? 'u-border-left' : ''
                } ${daysByWeek(calendarStart).length - 1 === weekIndex ? '' : 'u-border-bottom'}`}
              >
                {day && (
                  <EventDayCard
                    orgId={orgId}
                    dateString={day}
                    criticalDates={criticalDatesByDate ? criticalDatesByDate[day] : undefined}
                    calendarDisplay={CalendarDisplayType.MONTH}
                  />
                )}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
});
